import { selectAll, selectOne } from 'css-select';
import { render } from 'dom-serializer';
import { Element, isTag, isText } from 'domhandler';
import type { ChildNode, Node, Text } from 'domhandler';
import { removeElement, replaceElement } from 'domutils';
import { parseDocument } from 'htmlparser2';

const WHITESPACE_ONLY = /^[\s ]*$/;

function isWhitespaceTextNode(node: Node | null | undefined): node is Text {
	return !!node && isText(node) && WHITESPACE_ONLY.test(node.data);
}

// A `<p>` counts as empty if every child is whitespace, `<br>`, or a HugeRTE
// bogus placeholder — the shapes the auto-close artifact takes after the
// editor's serializer strips bogus markers.
function isEmptyParagraph(node: Node | null | undefined): node is Element {
	if (!node || !isTag(node) || node.tagName !== 'p') return false;
	return node.children.every((child) => {
		if (isText(child)) return WHITESPACE_ONLY.test(child.data);
		if (isTag(child)) return child.tagName === 'br' || 'data-mce-bogus' in child.attribs;
		return false;
	});
}

function getSibling(node: Node, direction: 'prev' | 'next'): ChildNode | null {
	return direction === 'prev' ? node.prev : node.next;
}

// htmlparser2 preserves whitespace text nodes between block elements (e.g.
// the `\n` between `</p>` and `<p>`), so when locating the adjacent paragraph
// we step over a single whitespace text node. We only strip ONE empty `<p>`
// per side — the HTML5 auto-close of `<p><div>…</div></p>` always produces
// exactly that one artifact, so anything further out (e.g. a user's
// Enter-Enter blank line) is preserved.
function removeAdjacentEmptyParagraph(node: Node, direction: 'prev' | 'next'): void {
	const adjacentSibling = getSibling(node, direction);
	const skippedWhitespace = isWhitespaceTextNode(adjacentSibling) ? adjacentSibling : null;
	const candidate = skippedWhitespace ? getSibling(skippedWhitespace, direction) : adjacentSibling;
	if (!isEmptyParagraph(candidate)) return;
	if (skippedWhitespace) removeElement(skippedWhitespace);
	removeElement(candidate);
}

export function removeRichMediaWrapper(html: string): string {
	const document = parseDocument(html);
	const matchingElements = [
		...selectAll<Node, Element>('div[class=mce-img]', document),
		...selectAll<Node, Element>('div[class=mce-iframe]', document),
	];
	matchingElements.forEach((matchingElement) => {
		const childElement = selectOne<Node, Element>('change-media', matchingElement);
		if (childElement) {
			// Wrap the unwrapped media in a `<p>` so it sits inside paragraph flow
			// just like the editor's `<p><div class="mce-img">…</div></p>` shape.
			// This lets the preview's `p + p { margin-top: 1.5em }` rule give the
			// media the same vertical rhythm as surrounding text paragraphs.
			const paragraphWrapper = new Element('p', {}, [childElement]);
			childElement.parent = paragraphWrapper;
			replaceElement(matchingElement, paragraphWrapper);
			// htmlparser2's HTML5 auto-close of `<p><div>…</div></p>` always leaves
			// exactly one empty `<p>` adjacent to the wrapper. Strip it so the
			// persisted HTML doesn't render a blank line; user-typed blanks live
			// one position further out and are preserved.
			removeAdjacentEmptyParagraph(paragraphWrapper, 'prev');
			removeAdjacentEmptyParagraph(paragraphWrapper, 'next');
		} else {
			removeElement(matchingElement);
		}
	});
	return render(document);
}
